/**
 * Accepting the Terms of service and Privacy policy: the checkbox signing up asks for, and the page that stands before
 * the app until someone signed in accepts the version in force ("Our terms changed", or, for an account made without
 * the sign-up page, "Accept our terms"). The server holds sessions to it too (`terms_required`).
 */

import { useEffect, useState } from "preact/hooks";
import type { LegalTexts } from "../../../../site/src/legal/types.ts";
import { api, logout, termsRequired } from "../../api/client.ts";
import type { MeTerms } from "../../api/types.gen.ts";
import { locale, negotiate, SLOT, t } from "../../i18n/index.ts";
import { ignore } from "../../lib/ignore.ts";
import { lazy } from "../../lib/lazy.ts";
import { legalUrls } from "../../lib/legal.ts";
import { loadMe, me } from "../../lib/state.ts";
import { Button } from "../../ui/Button.tsx";
import { Form } from "../../ui/form.tsx";
import { NewTabIcon } from "../../ui/icons.tsx";
import { Slotted } from "../../ui/misc.tsx";
import { Segmented } from "../../ui/Segmented.tsx";
import { Spinner } from "../../ui/Spinner.tsx";
import { LINK } from "../../ui/styles.ts";
import { useSubmit } from "./flow.ts";
import { AuthFrame, FormError } from "./frame.tsx";
import type { ShownDoc } from "./legalText.tsx";

/** The legal texts' chunks (a page opened before a deploy reloads for them: lib/lazy.ts). */
const legalChunk = lazy(async () => {
  const [{ LegalText }, { loadLegal }] = await Promise.all([import("./legalText.tsx"), import("./legalTexts.ts")]);
  return { LegalText, loadLegal };
});

/** A link to a legal document, in a new tab (the form stays). */
function Doc({ href, children }: { href: string; children: string }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" class={LINK}>
      {children}
    </a>
  );
}

/** "I accept the Terms of service and the Privacy policy", each a link to the site's, in the reader's language. */
export function TermsCheckbox({ checked, onCheck }: { checked: boolean; onCheck: (on: boolean) => void }) {
  const legal = legalUrls(locale.value);
  return (
    <label class="flex items-start gap-2 text-sm text-zinc-800">
      <input
        type="checkbox"
        required={true}
        class="mt-0.5 size-4 shrink-0 rounded border-zinc-300 accent-blue-600"
        checked={checked}
        onChange={(e) => onCheck(e.currentTarget.checked)}
      />
      <span>
        <Slotted text={t("terms.accept", { terms: SLOT, privacy: SLOT })}>
          {[
            <Doc key="terms" href={legal.terms}>
              {t("legal.terms")}
            </Doc>,
            <Doc key="privacy" href={legal.privacy}>
              {t("legal.privacy")}
            </Doc>,
          ]}
        </Slotted>
      </span>
    </label>
  );
}

/** The panel's text, once loaded: in which language, and what shows it. */
type Loaded = { lang: string; view: typeof import("./legalText.tsx").LegalText; texts: LegalTexts };

/**
 * The Terms and the Privacy policy, to read here (a tab each, in a panel of their own that scrolls), or on the site
 * in a new tab. The text loads with this page only, in the reader's language.
 */
function LegalPanel() {
  const lang = negotiate([locale.value]);
  const urls = legalUrls(lang);
  const [page, setPage] = useState<ShownDoc>("terms");
  const [loaded, setLoaded] = useState<Loaded | "failed" | null>(null);
  useEffect(() => {
    let live = true;
    const load = async (): Promise<Loaded | "failed"> => {
      try {
        const { LegalText, loadLegal } = await legalChunk();
        return { lang, view: LegalText, texts: await loadLegal(lang) };
      } catch {
        return "failed";
      }
    };
    load().then((l) => live && setLoaded(l), ignore);
    return () => {
      live = false;
    };
  }, [lang]);
  const name = t(page === "terms" ? "legal.terms" : "legal.privacy");
  let body = <Spinner />;
  if (loaded === "failed") {
    body = <p class="text-sm text-zinc-600">{t("terms.unavailable")}</p>;
  } else if (loaded?.lang === lang) {
    const View = loaded.view;
    body = <View texts={loaded.texts} page={page} urls={urls} onShow={setPage} />;
  }
  return (
    <div class="flex min-h-0 flex-1 flex-col gap-2">
      <div class="flex items-center justify-between gap-3">
        <Segmented
          label={t("terms.documents")}
          options={[
            { value: "terms", label: t("legal.terms") },
            { value: "privacy", label: t("legal.privacy") },
          ]}
          value={page}
          onValue={setPage}
        />
        <a
          href={urls[page]}
          target="_blank"
          rel="noopener noreferrer"
          title={t("terms.newTab")}
          class={`inline-flex items-center gap-1 text-sm ${LINK}`}
        >
          <NewTabIcon />
          <span class="sr-only sm:not-sr-only">{t("terms.newTab")}</span>
        </a>
      </div>
      <section
        key={page}
        aria-label={name}
        // biome-ignore lint/a11y/noNoninteractiveTabindex: a region that scrolls takes the keyboard's focus to scroll it.
        tabIndex={0}
        class="min-h-48 flex-1 overflow-y-auto overscroll-contain rounded-lg border border-zinc-200 bg-white px-4 py-4 shadow-xs focus-visible:outline-2 focus-visible:outline-blue-600 sm:px-5"
      >
        {body}
      </section>
    </div>
  );
}

/** Before the app: the Terms in force to read and accept (or signing out). */
export function AcceptTerms() {
  const [accepted, setAccepted] = useState(false);
  const terms = me.value?.terms;
  const changed = terms?.accepted_earlier ?? false;
  const accept = useSubmit(async () => {
    await api.post<MeTerms>("/me/terms", { version: terms?.version ?? "" });
    termsRequired.value = false;
    await loadMe();
  });
  return (
    <AuthFrame
      title={t(changed ? "terms.changedTitle" : "terms.newTitle")}
      lede={t(changed ? "terms.changedLede" : "terms.newLede")}
      reader={true}
      account={{
        email: me.value?.email ?? me.value?.name ?? "",
        onSignOut: () => {
          logout().catch(ignore);
        },
      }}
    >
      <FormError error={accept.error} />
      <Form class="flex min-h-0 flex-1 flex-col" onSubmit={accept.submit}>
        <LegalPanel />
        {/* Kept in view at the bottom, whatever the panel's length (and above a phone's home indicator). */}
        <div class="sticky bottom-0 mt-3 flex flex-col gap-4 bg-sheet pt-3 pb-[max(1rem,env(safe-area-inset-bottom))] sm:mt-4 sm:pb-[max(1.5rem,env(safe-area-inset-bottom))]">
          <TermsCheckbox checked={accepted} onCheck={setAccepted} />
          <Button variant="primary" submit={true} busy={accept.busy} class="w-full">
            {t("auth.continue")}
          </Button>
        </div>
      </Form>
    </AuthFrame>
  );
}
